import { GET_CLEANUP_WIZARD_REASON_FILTER } from '@/api';
import { cleanupWizardService } from '@/api/cleanupWizard';
import { Button, Icon, Typography } from '@/components/common';
import { InputText } from '@/components/form';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import Icons from '@/const/icons';
import { useNotification } from '@/hooks';
import { Option } from '@/interfaces';
import { CheckIcon } from '@heroicons/react/24/solid';
import { PopoverContent } from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { X } from 'lucide-react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';

interface FlagClientProps {
	align?: 'start' | 'end' | 'center';
	side?: 'left' | 'right' | 'top' | 'bottom';
	clientId?: string;
	refetchData?: (options?: any) => Promise<any>;
}

const FlagClient: React.FC<FlagClientProps> = ({
	clientId,
	align,
	side,
	refetchData,
}) => {
	const { register, handleSubmit, setValue, watch, resetField } = useForm();
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [isInputOpen, setIsInputOpen] = React.useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
	const [searchTerm, setSearchTerm] = React.useState<string>('');
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const { toastSuccess, toastError } = useNotification();

	const { data: reasonsFilterList } = useQuery({
		queryKey: [GET_CLEANUP_WIZARD_REASON_FILTER],
		queryFn: () => cleanupWizardService.getReasonFilter(),
		refetchOnWindowFocus: false,
	});

	const mobileListRef = useRef<HTMLDivElement>(null);
	const desktopListRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const currentReason = watch('reason');

	// Función para detectar si es móvil o desktop
	useEffect(() => {
		const checkIfMobile = () => {
			setIsMobile(window.innerWidth < 1024);
		};

		checkIfMobile();

		const handleResize = () => checkIfMobile();
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const reasonsOptions: Option[] = React.useMemo(() => {
		if (!reasonsFilterList) return [];
		return reasonsFilterList.data.map((reason: string) => ({
			label: reason || '',
			name: reason || '',
		}));
	}, [reasonsFilterList]);

	const filteredOptions = React.useMemo(() => {
		if (!searchTerm) return reasonsOptions;
		return reasonsOptions.filter((option) =>
			option.name.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [searchTerm, reasonsOptions]);

	const onSubmit = useCallback(
		async (data: any) => {
			if (!clientId) return;
			try {
				setIsSubmitting(true);
				await cleanupWizardService.flagClient(clientId, data.reason);
				toastSuccess('Flagged for review successfully.');
				refetchData && refetchData();
			} catch (error) {
				toastError('Couldnt flag for review. Please try again.');
			} finally {
				setIsSubmitting(false);
				setIsOpen(false);
			}
		},
		[clientId, refetchData, toastError, toastSuccess]
	);

	const handleSelect = useCallback(
		(option: Option, isMobileVersion: boolean) => {
			setValue('reason', option.name);
			if (!isMobileVersion) {
				setIsInputOpen(false);
			}
		},
		[setValue]
	);

	const handleSearch = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			setSearchTerm(value);
			setValue('search', value);
		},
		[setValue]
	);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		setSearchTerm('');
		resetField('search');
		setIsInputOpen(false);
	}, [resetField]);

	// Memoize the mobile version component
	const MobileVersion = React.useMemo(
		() => (
			<Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
				<Dialog.Trigger asChild>
					<Button
						decoration="secondary"
						label="Flag for Review"
						size="medium"
						icon={Icons.flag}
						iconRight
						className="!py-[14.5px] !border !border-[#FF0043] !text-[#FF0043] !bg-white w-full md:w-auto"
						type="button"
					/>
				</Dialog.Trigger>

				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-[#474747CC] z-40" />
					<Dialog.Content
						className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] md:w-[400px] py-6 px-4 border shadow-lg rounded-[24px] bg-white z-50"
						ref={dialogRef}
					>
						<div className="h-full flex flex-col">
							<div className="flex justify-between items-center mb-6 relative">
								<Dialog.Title asChild>
									<Typography type="semibold-20" className="w-full text-center">
										Flag for Review
									</Typography>
								</Dialog.Title>
								<Dialog.Close asChild>
									<button
										className="absolute right-0 p-1 hover:bg-gray-100 rounded-full"
										onClick={handleClose}
									>
										<X width={20} height={20} className="text-gray-600" />
									</button>
								</Dialog.Close>
							</div>

							<div className="mb-3">
								<Typography type="regular-16" className="mb-4">
									Flagging reason
								</Typography>
								<InputText
									name="search"
									register={register}
									placeholder="Search"
									iconLeft={Icons.search}
									className="w-full 2xl:w-auto"
									classNameIcon={clsx('!top-[25%] !w-4 !h-4 !text-[#0D061699]')}
									classNameInput={clsx(
										'pl-9 border-none !bg-gray-15 h-8 !mt-0'
									)}
									onChange={handleSearch}
								/>
							</div>

							<div
								className="flex-1 overflow-auto max-h-[300px] scroll-custom pr-1"
								ref={mobileListRef}
							>
								{filteredOptions.map((option, index) => (
									<div
										className={clsx(
											'flex items-center justify-between w-full gap-2 group px-4 hover:!bg-grey-100 cursor-pointer',
											{ 'bg-grey-100': option.name === currentReason }
										)}
										key={index}
										onClick={() => handleSelect(option, true)}
									>
										<div className="relative flex gap-1 py-3">
											<Typography
												type="regular-16"
												className="line-clamp-1 text-ellipsis"
											>
												{option.label}
											</Typography>
										</div>
										{currentReason === option.label && (
											<span className="">
												<CheckIcon
													className="w-5 h-5 text-black"
													aria-hidden="true"
												/>
											</span>
										)}
									</div>
								))}
							</div>

							<div className="mt-6">
								<Button
									label="Confirm"
									decoration="primary"
									type="submit"
									className={clsx(
										'mt-6 max-w-[116px] mx-auto',
										!currentReason && 'opacity-50'
									)}
									onClick={handleSubmit(onSubmit)}
									disabled={isSubmitting || !currentReason}
									loading={isSubmitting}
								/>
							</div>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		),
		[
			isOpen,
			currentReason,
			filteredOptions,
			handleClose,
			handleSearch,
			handleSelect,
			handleSubmit,
			isSubmitting,
			onSubmit,
			register,
		]
	);

	// Versión desktop (>= 1024px)
	const DesktopVersion = () => (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<div>
					<Button
						decoration="secondary"
						label="Flag for Review"
						size="medium"
						icon={Icons.flag}
						iconRight
						className="!py-[14.5px] !border !border-[#FF0043] !text-[#FF0043] !bg-white w-full md:w-auto"
						type="button"
						onClick={() => setIsOpen(true)}
					/>
				</div>
			</PopoverTrigger>
			<PopoverContent
				align={align}
				side={side}
				className={clsx(
					'w-[474px] !py-6 !px-8 bg-white border shadow-sm rounded-[32px] my-2 !z-[9999] bg-gradient-to-b from-[#FFFFFF] to-[#F4F4F9]'
				)}
				onPointerDownOutside={() => {
					setIsOpen(false);
					setIsInputOpen(false);
				}}
			>
				<div className="h-full flex flex-col">
					<div className="flex justify-between items-center mb-6">
						<Typography type="semibold-20">Flag for Review</Typography>
						<X
							width={24}
							height={24}
							className="cursor-pointer"
							onClick={handleClose}
						/>
					</div>

					<Typography type="regular-16" className="mb-2">
						Flagging reason
					</Typography>
					<div
						className="flex items-center justify-between h-12 rounded-[100px] border border-opacity-primary pl-6 py-3 pr-[14px] cursor-pointer bg-white"
						onClick={() => setIsInputOpen((prev) => !prev)}
					>
						<Typography
							type="regular-16"
							className={clsx(
								'',
								!currentReason ? 'text-opacity-primary' : 'text-black'
							)}
						>
							{currentReason || 'Select'}
						</Typography>
						<Icon
							src={Icons.arrowDown}
							width={20}
							height={20}
							className={clsx(
								'cursor-pointer',
								isInputOpen ? 'rotate-180 transform' : ''
							)}
						/>
					</div>
					{isInputOpen && (
						<div
							className="mt-2 h-[268px] rounded-3xl border border-opacity-primary py-4 bg-white"
							ref={desktopListRef}
						>
							<InputText
								name="search"
								register={register}
								placeholder="Search"
								iconLeft={Icons.search}
								className="w-full 2xl:w-auto px-4"
								classNameIcon={clsx('!top-[25%] !w-4 !h-4 !text-[#0D061699]')}
								classNameInput={clsx('pl-9 border-none !bg-gray-15 h-8 !mt-0')}
								onChange={handleSearch}
							/>
							<div
								className="h-[192px] overflow-auto scroll-custom mt-3"
								ref={desktopListRef}
							>
								{filteredOptions.map((option, index) => (
									<div
										className={clsx(
											'flex items-center justify-between px-4 w-full gap-2 group hover:!bg-[#F4F4F4A3] cursor-pointer',
											{ 'bg-grey-100': option.name === currentReason }
										)}
										key={index}
										onClick={() => handleSelect(option, false)}
									>
										<div className="relative flex gap-1 py-3">
											<Typography
												type="regular-16"
												className="line-clamp-1 text-ellipsis"
											>
												{option.label}
											</Typography>
										</div>
										{currentReason === option.label && (
											<span className="">
												<CheckIcon
													className="w-5 h-5 text-black"
													aria-hidden="true"
												/>
											</span>
										)}
									</div>
								))}
							</div>
						</div>
					)}
					<Button
						label="Confirm"
						decoration="primary"
						type="submit"
						className={clsx('mt-6', !currentReason && 'opacity-50')}
						onClick={handleSubmit(onSubmit)}
						disabled={isSubmitting || !currentReason}
						loading={isSubmitting}
					/>
				</div>
			</PopoverContent>
		</Popover>
	);

	// Renderizar condicionalmente basado en tamaño de pantalla
	return isMobile ? MobileVersion : <DesktopVersion />;
};

export default FlagClient;
